import { useQuery } from '@tanstack/react-query'
import { CheckCircle2, CircleAlert } from 'lucide-react'
import { qk } from '@/api/query-keys'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { pipelineApi, usePipelineBoard, usePipelineSettings, usePipelineWrite, type Settings } from './api'
import { DAYS, PipelineFrame } from './shared'

/** T8 / T6 — the pipeline's switches, and who in GoHighLevel is who in Tapwork. */
export function SettingsPage() {
  const settings = usePipelineSettings()
  const can = usePipelineBoard().data?.can
  const people = useQuery({ queryKey: qk.pipelinePeople(), queryFn: pipelineApi.people, staleTime: 5 * 60_000 })
  const save = usePipelineWrite((patch: Partial<Settings>) => pipelineApi.saveSettings(patch), () => 'Saved')
  const s = settings.data
  const editable = Boolean(can?.configure)
  return (
    <PipelineFrame title="Pipeline settings" description="What the pipeline is allowed to do outside Tapwork. Both outside switches start off.">
      {settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : settings.isPending || !s ? (
        <LoadingState label="Loading settings" />
      ) : (
        <div className="grid max-w-3xl gap-5">
          {!editable ? <p className="text-sm text-muted-foreground">Read-only: changing these needs “Set up the CRM playbook”.</p> : null}
          <section className="grid gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
            <Toggle
              label="Write to GoHighLevel"
              help="Moves, values and check-in notes go to GHL too. Off: they are kept in Tapwork and marked “Not in GHL”. Leave off on staging: staging is connected to the live GoHighLevel account."
              checked={s.ghlWrites}
              disabled={!editable || save.isPending}
              onChange={(v) => save.mutate({ ghlWrites: v })}
              warn={!s.production && s.ghlWrites}
            />
            <Toggle
              label="Send Slack messages"
              help="The Friday check-in DM, the Monday report post, decision-overdue alerts to the CEO and owner, won-deal handoffs and after-meeting prompts. Off: everything stays in the app. “Send it to me” buttons still work, to you only."
              checked={s.slack}
              disabled={!editable || save.isPending}
              onChange={(v) => save.mutate({ slack: v })}
              warn={!s.production && s.slack}
            />
            <Toggle
              label="Open the Client Project at Pilot agreed"
              help="When the client is already known for the company, moving a deal to Pilot agreed opens its Client Project in Pilot. Otherwise the deal asks for the client."
              checked={s.autoOpenProject}
              disabled={!editable || save.isPending}
              onChange={(v) => save.mutate({ autoOpenProject: v })}
            />
            {s.production ? (
              <Toggle label="Pipeline on in production" help="Shows the pipeline pages and runs its jobs here." checked={s.enabled} disabled={!editable || save.isPending} onChange={(v) => save.mutate({ enabled: v })} />
            ) : (
              <p className="text-xs text-muted-foreground">This is not production, so the pipeline is always on here. In production it stays off until switched on.</p>
            )}
          </section>

          <section className="grid gap-4 rounded-xl border border-border bg-card p-4 shadow-sm sm:grid-cols-2">
            <When label="Friday check-in" day={s.checkinDay} hour={s.checkinHour} disabled={!editable} onChange={(day, hour) => save.mutate({ checkinDay: day, checkinHour: hour })} />
            <When label="Monday report" day={s.reportDay} hour={s.reportHour} disabled={!editable} onChange={(day, hour) => save.mutate({ reportDay: day, reportHour: hour })} />
            <label className="grid gap-1 text-sm sm:col-span-2">
              <span className="font-medium">Report channel</span>
              <span className="text-xs text-muted-foreground">Slack channel ID (C…). Empty: the report is not posted, only shown on the Monday report page.</span>
              <Input defaultValue={s.reportChannelId} disabled={!editable} className="w-56" placeholder="C0123456789" onBlur={(e) => e.target.value.trim() !== s.reportChannelId && save.mutate({ reportChannelId: e.target.value.trim() })} />
            </label>
          </section>

          <section className="rounded-xl border border-border bg-card p-4 shadow-sm">
            <h2 className="text-sm font-semibold">People</h2>
            <p className="mb-3 text-xs text-muted-foreground">A GoHighLevel user is a Tapwork member when the emails match. Unmatched owners get no check-in and their deals show on nobody's “Mine”.</p>
            {people.data ? (
              <ul className="grid gap-1.5 text-sm">
                {people.data.users.map((u) => (
                  <li key={u.ghlUserId} className="flex items-center gap-2">
                    {u.member ? <CheckCircle2 className="size-4 text-success" /> : <CircleAlert className="size-4 text-warning" />}
                    <span className="font-medium">{u.name}</span>
                    <span className="text-muted-foreground">{u.email ?? 'no email in GHL'}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{u.member ? `Tapwork: ${u.member.name}` : 'Not in Tapwork'}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <LoadingState label="Matching people" />
            )}
          </section>
        </div>
      )}
    </PipelineFrame>
  )
}

function Toggle({ label, help, checked, disabled, onChange, warn }: { label: string; help: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void; warn?: boolean }) {
  return (
    <label className="flex items-start justify-between gap-4">
      <span>
        <span className="block text-sm font-medium">{label}</span>
        <span className="block text-xs text-muted-foreground">{help}</span>
        {warn ? <span className="mt-1 block text-xs font-medium text-danger">On, on staging: this reaches the live account and real people.</span> : null}
      </span>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </label>
  )
}

function When({ label, day, hour, disabled, onChange }: { label: string; day: number; hour: number; disabled: boolean; onChange: (day: number, hour: number) => void }) {
  return (
    <div className="grid gap-1 text-sm">
      <span className="font-medium">{label}</span>
      <div className="flex gap-2">
        <select value={day} disabled={disabled} onChange={(e) => onChange(Number(e.target.value), hour)} className="h-9 rounded-md border border-input bg-background px-2 text-sm" aria-label={`${label} day`}>
          {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
        </select>
        <select value={hour} disabled={disabled} onChange={(e) => onChange(day, Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-2 text-sm" aria-label={`${label} hour`}>
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
        </select>
      </div>
      <span className="text-xs text-muted-foreground">Manila time</span>
    </div>
  )
}
